import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Plus, List, Columns3, FolderTree, Archive, Merge, Search, Sparkles, Loader2 } from 'lucide-react'
import { useUser } from '../hooks/useUser'
import { FeedbackFilters, type FeedbackFilterState } from '../components/feedback/FeedbackFilters'
import { FeedbackList } from '../components/feedback/FeedbackList'
import { FeedbackKanban } from '../components/feedback/FeedbackKanban'
import { FeedbackThemes } from '../components/feedback/FeedbackThemes'
import { FeedbackInsights } from '../components/feedback/FeedbackInsights'
import { ThemeDrawer } from '../components/feedback/ThemeDrawer'
import { FeedbackForm } from '../components/feedback/FeedbackForm'
import { FeedbackDrawer } from '../components/feedback/FeedbackDrawer'
import { ThemeDialog } from '../components/feedback/ThemeDialog'
import { AdminMergeDialog } from '../components/feedback/AdminMergeDialog'
import type { FeedbackItem, AISummaryResponse } from '../lib/feedbackApi'

type ViewMode = 'list' | 'board' | 'themes' | 'insights'
type MergeResult = {
  mergedItem: FeedbackItem
  sourceIds: number[]
  nonce: number
}

const DEFAULT_FILTERS: FeedbackFilterState = {
  type: null,
  status: null,
  mine: false,
  search: '',
  sort: 'newest',
}

export default function Feedback() {
  const [searchParams, setSearchParams] = useSearchParams()
  const user = useUser()
  const [view, setView] = useState<ViewMode>('list')
  const [filters, setFilters] = useState<FeedbackFilterState>(DEFAULT_FILTERS)
  const [showForm, setShowForm] = useState(false)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [detailRefreshKey, setDetailRefreshKey] = useState(0)
  const [createdItem, setCreatedItem] = useState<FeedbackItem | null>(null)
  const [themeDialogIds, setThemeDialogIds] = useState<number[]>([])
  const [showThemeDialog, setShowThemeDialog] = useState(false)
  const [showMergeDialog, setShowMergeDialog] = useState(false)
  const [mergeTarget, setMergeTarget] = useState<FeedbackItem | null>(null)
  const [mergeResult, setMergeResult] = useState<MergeResult | null>(null)
  const [showArchive, setShowArchive] = useState(false)
  const [drawerWidth, setDrawerWidth] = useState(0)
  const [selectedThemeId, setSelectedThemeId] = useState<number | null>(null)
  const [autoGroupTrigger, setAutoGroupTrigger] = useState(0)
  const [autoGroupInfo, setAutoGroupInfo] = useState({ generating: false, ungroupedCount: 0 })
  const [cachedInsights, setCachedInsights] = useState<AISummaryResponse | null | undefined>(undefined)

  const handleDrawerWidthChange = useCallback((w: number) => {
    setDrawerWidth(w)
  }, [])

  // Auto-open form when ?new=true
  useEffect(() => {
    if (searchParams.get('new') === 'true') {
      setShowForm(true)
      // Remove the param so refreshing doesn't re-open
      setSearchParams({}, { replace: true })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const bumpListRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1)
  }, [])

  const bumpDetailRefresh = useCallback(() => {
    setDetailRefreshKey((k) => k + 1)
  }, [])

  const bumpAllRefresh = useCallback(() => {
    bumpListRefresh()
    bumpDetailRefresh()
  }, [bumpListRefresh, bumpDetailRefresh])

  function handleCreated(item: FeedbackItem) {
    setCreatedItem(item)
    setRefreshKey((k) => k + 1)
  }

  function handleGroupSelected(ids: number[]) {
    setThemeDialogIds(ids)
    setShowThemeDialog(true)
  }

  function handleExternalVoteChange(id: number) {
    if (id === selectedId) {
      bumpDetailRefresh()
    }
  }

  function openMergeDialog(targetItem?: FeedbackItem | null) {
    setMergeTarget(targetItem ?? null)
    setShowMergeDialog(true)
  }

  function closeMergeDialog() {
    setShowMergeDialog(false)
    setMergeTarget(null)
  }

  return (
    <div className="space-y-6 transition-[margin] duration-200" style={{ marginRight: drawerWidth ? `${drawerWidth}px` : undefined }}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Feedback</h1>
          <p className="text-sm text-muted-foreground mt-1 hidden sm:block">
            Share ideas, report bugs, and vote on what matters most
          </p>
        </div>
        <div className="flex items-center gap-2">
          {user.isAdmin && (
            <button
              onClick={() => openMergeDialog()}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 border border-border rounded-lg text-sm font-medium hover:bg-secondary transition-colors"
            >
              <Merge className="w-4 h-4" />
              <span className="hidden sm:inline">Merge</span>
            </button>
          )}
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">New Feedback</span>
          </button>
        </div>
      </div>

      {/* View toggle + filters */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center border border-border rounded-lg overflow-hidden">
            <button
              onClick={() => { setView('list'); setShowArchive(false) }}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${
                view === 'list' && !showArchive
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-secondary'
              }`}
            >
              <List className="w-4 h-4" />
              List
            </button>
            <button
              onClick={() => { setView('board'); setShowArchive(false) }}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${
                view === 'board' && !showArchive
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-secondary'
              }`}
            >
              <Columns3 className="w-4 h-4" />
              Board
            </button>
            {user.isAdmin && (
              <button
                onClick={() => { setView('themes'); setShowArchive(false) }}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${
                  view === 'themes' && !showArchive
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-secondary'
                }`}
              >
                <FolderTree className="w-4 h-4" />
                Themes
              </button>
            )}
            {user.isAdmin && (
              <button
                onClick={() => { setView('insights'); setShowArchive(false) }}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${
                  view === 'insights' && !showArchive
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-secondary'
                }`}
              >
                <Search className="w-4 h-4" />
                Insights
              </button>
            )}
          </div>

          {/* Archive — acts as a separate view */}
          {user.isAdmin && (
            <button
              onClick={() => {
                if (showArchive) {
                  setShowArchive(false)
                } else {
                  setShowArchive(true)
                  setView('list')
                }
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                showArchive
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border hover:bg-secondary text-muted-foreground'
              }`}
            >
              <Archive className="w-3.5 h-3.5" />
              Archive
            </button>
          )}
        </div>

        {view !== 'insights' && (
          <FeedbackFilters
            filters={filters}
            onChange={setFilters}
            isAdmin={user.isAdmin}
          >
            {view === 'themes' && user.isAdmin && (
              <button
                onClick={() => setAutoGroupTrigger((n) => n + 1)}
                disabled={autoGroupInfo.generating || autoGroupInfo.ungroupedCount === 0}
                className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-border hover:bg-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                title={autoGroupInfo.ungroupedCount === 0 ? 'No ungrouped items to analyze' : undefined}
              >
                {autoGroupInfo.generating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                Auto-group
              </button>
            )}
          </FeedbackFilters>
        )}
      </div>

      {/* Content */}
      {view === 'list' ? (
        <FeedbackList
          key={`${refreshKey}-${showArchive}`}
          filters={filters}
          createdItem={createdItem}
          mergeResult={mergeResult}
          showArchive={showArchive}
          onSelect={setSelectedId}
          onNewFeedback={() => setShowForm(true)}
          onGroupSelected={user.isAdmin ? handleGroupSelected : undefined}
          onRefresh={bumpListRefresh}
          onVoteChange={handleExternalVoteChange}
        />
      ) : view === 'board' ? (
        <FeedbackKanban
          key={refreshKey}
          isAdmin={user.isAdmin}
          onCardClick={setSelectedId}
          refreshKey={refreshKey}
          mergeResult={mergeResult}
          filters={filters}
          showArchive={showArchive}
        />
      ) : view === 'themes' ? (
        <FeedbackThemes
          isAdmin={user.isAdmin}
          onItemSelect={setSelectedId}
          onThemeSelect={setSelectedThemeId}
          refreshKey={refreshKey}
          mergeResult={mergeResult}
          filters={filters}
          showArchive={showArchive}
          onVoteChange={handleExternalVoteChange}
          autoGroupTrigger={autoGroupTrigger}
          onAutoGroupInfo={setAutoGroupInfo}
        />
      ) : (
        <FeedbackInsights
          onItemSelect={setSelectedId}
          cachedData={cachedInsights}
          onDataChange={setCachedInsights}
        />
      )}

      {/* Detail drawer */}
      <FeedbackDrawer
        feedbackId={selectedId}
        refreshKey={detailRefreshKey}
        onClose={() => setSelectedId(null)}
        onStatusChange={bumpAllRefresh}
        onVoteChange={() => setRefreshKey((k) => k + 1)}
        onWidthChange={handleDrawerWidthChange}
        onMergeRequested={openMergeDialog}
      />

      {/* Theme detail drawer */}
      <ThemeDrawer
        themeId={selectedThemeId}
        onClose={() => setSelectedThemeId(null)}
        onItemSelect={(id) => { setSelectedThemeId(null); setSelectedId(id) }}
        onWidthChange={handleDrawerWidthChange}
      />

      {/* Form modal */}
      <FeedbackForm
        open={showForm}
        onClose={() => setShowForm(false)}
        onCreated={handleCreated}
      />

      {/* Theme grouping dialog */}
      <ThemeDialog
        open={showThemeDialog}
        feedbackIds={themeDialogIds}
        onClose={() => setShowThemeDialog(false)}
        onDone={bumpAllRefresh}
      />

      <AdminMergeDialog
        open={showMergeDialog}
        targetItem={mergeTarget}
        onClose={closeMergeDialog}
        onMerged={(mergedItem, sourceIds) => {
          setMergeResult({ mergedItem, sourceIds, nonce: Date.now() })
          setSelectedId((current) => (current && sourceIds.includes(current) ? mergedItem.id : current))
          bumpAllRefresh()
          window.setTimeout(() => bumpAllRefresh(), 400)
        }}
      />
    </div>
  )
}
